import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type {
    BehaviorMapEventHandler,
    DrawingStyle,
    EasingFunctionDescription,
    LngLat,
    LngLatBounds,
    YMap,
    YMapBoundsLocation,
} from '@yandex/ymaps3-types';
import { useYandexMapsApi } from '@adv-frontend/yandex-maps-kit';
import { type GeoObject } from '@/shared/api/geoCoder';
import { GeoLocationControl } from '@/shared/components/GeoLocationControl';
import { ZoomControls } from '@/shared/components/ZoomControls';
import { useGeoLocation } from '@/shared/lib/hooks';
import { DEFAULT_POINT, DEFAULT_ZOOM, withYandexMapsApiProvider } from '@/shared/lib/yandexMap';
import { buildYMapLocation } from '@/shared/lib/yandexMap';
import pinMarkerCurrentPossitionIconSource from './assets/currentPosition.svg';
import { useRoute } from './lib/hooks/useRoute';
import { usePickupAddressContext } from '../../PickupAddressContext';
import { PickupAddressMapCluster } from '../PickupAddressMapCluster';
import styles from './PickupAddressMap.module.css';

const DEFAULT_ZOOM_RANGE = { min: 1, max: 21 };
const MAP_MARGING: [number, number, number, number] = [50, 50, 50, 50];

const LINE_STYLE: DrawingStyle = {
    simplificationRate: 0,
    stroke: [{ color: '#21201f', dash: [8, 8], width: 4 }],
};

const PickupAddressMapListener = () => {
    const mapsApi = useYandexMapsApi();
    const { onUpdateBounds } = usePickupAddressContext();
    const onUpdate = useCallback(
        ({ location }: { location: YMapBoundsLocation }) => {
            onUpdateBounds(location.bounds);
        },
        [onUpdateBounds]
    );

    if (!mapsApi.maps?.YMapListener) {
        return null;
    }

    return <mapsApi.maps.YMapListener onUpdate={onUpdate} onActionEnd={onUpdate} />;
};

interface PickupAddressMapProps {
    locations: GeoObject[];
}

export const PickupAddressMap = withYandexMapsApiProvider(function PickupAddressMap(props: PickupAddressMapProps) {
    const { locations } = props;
    const ymapRef = useRef<null | YMap>(null);
    const sourceId = useId();
    const mapsApi = useYandexMapsApi();
    const [coordinates, setCoordinates] = useState<LngLat | null>(null);
    const [zoom, setZoom] = useState<number>();
    const [routeBounds, setRouteBounds] = useState<LngLatBounds | null>(null);
    const [pickupPontClustered, setPickupPontClustered] = useState<boolean>(false);

    const { pickupPoint } = usePickupAddressContext();

    const onUpdatePosition = useCallback((coordinates: LngLat, zoomDelta?: number) => {
        if (!ymapRef.current) {
            return;
        }

        if (zoomDelta) {
            setCoordinates(coordinates);
            setZoom(ymapRef.current.zoom + zoomDelta);
            setRouteBounds(null);
        }
    }, []);

    const onZoomUpClick = useCallback(() => {
        if (ymapRef.current?.zoom) {
            setZoom(ymapRef.current?.zoom + 1);
            setCoordinates([ymapRef.current?.center[0], ymapRef.current?.center[1]]);
            setRouteBounds(null);
        }
    }, []);

    const onZoomDownClick = useCallback(() => {
        if (ymapRef.current?.zoom) {
            setZoom(ymapRef.current?.zoom - 1);
            setCoordinates([ymapRef.current?.center[0], ymapRef.current?.center[1]]);
            setRouteBounds(null);
        }
    }, []);

    const geoLocation = useGeoLocation();

    const onGeolocatePosition = useCallback(() => {
        if (geoLocation) {
            setCoordinates([geoLocation.longitude, geoLocation.latitude]);
            setZoom(DEFAULT_ZOOM);
            setPickupPontClustered(false);
        }
    }, [geoLocation]);

    useEffect(() => {
        if (locations[0]) {
            const defaultLocation = buildYMapLocation(locations[0]);

            setCoordinates(defaultLocation.center);
            setZoom(defaultLocation.zoom);
            setRouteBounds(null);
        }
    }, [locations]);

    const { route, loadRoute } = useRoute({
        pickupPointId: pickupPoint?.id,
        currentPositionCoords: geoLocation,
        setCoordinates,
        setZoom,
        setRouteBounds,
    });

    useEffect(() => {
        if (!geoLocation) {
            if (pickupPoint?.id) {
                setCoordinates([pickupPoint.position.longitude, pickupPoint.position.latitude]);
                setZoom(DEFAULT_ZOOM);
                setRouteBounds(null);
            }

            return;
        }

        loadRoute(pickupPoint?.id, pickupPoint?.position);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- position может измениться при масштабировании
    }, [loadRoute, pickupPoint?.id]);

    const createBehaviorEventHandler = (): BehaviorMapEventHandler => (event) => {
        if (event.type === 'scrollZoom' || event.type === 'oneFingerZoom' || event.type === 'drag') {
            setCoordinates(event.location.center);
            setZoom(event.location.zoom);
            setRouteBounds(null);
        }
    };

    const location:
        | { zoom: number; center: LngLat }
        | { duration: number; easing: EasingFunctionDescription; bounds: LngLatBounds }
        | { duration: number; easing: EasingFunctionDescription; center: LngLat; zoom: number } = useMemo(() => {
        if (routeBounds) {
            return {
                duration: 200,
                easing: 'ease-in-out',
                bounds: routeBounds,
            };
        }

        if (zoom && coordinates) {
            return {
                duration: 200,
                easing: 'ease-in-out',
                center: coordinates,
                zoom,
            };
        }

        return DEFAULT_POINT;
    }, [coordinates, routeBounds, zoom]);

    if (!mapsApi.maps?.YMap) {
        return null;
    }

    return (
        <mapsApi.maps.YMap
            margin={MAP_MARGING}
            ref={ymapRef}
            location={location}
            zoomStrategy="zoomToCenter"
            zoomRange={DEFAULT_ZOOM_RANGE}
        >
            <mapsApi.maps.YMapDefaultFeaturesLayer />
            <PickupAddressMapListener />
            <mapsApi.maps.YMapDefaultSchemeLayer />
            <mapsApi.maps.YMapFeatureDataSource id={sourceId} />
            <PickupAddressMapCluster
                ymapRef={ymapRef}
                sourceId={sourceId}
                onUpdatePosition={onUpdatePosition}
                setPickupPontClustered={setPickupPontClustered}
                // На низких уровнях зума уменьшаем область кластеризации для вывода большего кол-во пинов без кластеризации
                gridSize={zoom && zoom > 13 ? 32 : 128}
            />
            <mapsApi.maps.YMapLayer source={sourceId} type="markers" />
            {route && !pickupPontClustered && <mapsApi.maps.YMapFeature {...route} style={LINE_STYLE} />}
            {geoLocation && (
                <mapsApi.maps.YMapMarker coordinates={[geoLocation.longitude, geoLocation.latitude]} zIndex={1}>
                    <img
                        className={styles.currentPositionPin}
                        width="28"
                        src={pinMarkerCurrentPossitionIconSource}
                        alt=""
                    />
                </mapsApi.maps.YMapMarker>
            )}
            <mapsApi.maps.YMapControls position="right" orientation="vertical">
                <mapsApi.maps.YMapControl transparent>
                    <ZoomControls onZoomUpClick={onZoomUpClick} onZoomDownClick={onZoomDownClick} />
                </mapsApi.maps.YMapControl>
                {geoLocation && (
                    <mapsApi.maps.YMapControl transparent>
                        <GeoLocationControl onGeolocatePosition={onGeolocatePosition} />
                    </mapsApi.maps.YMapControl>
                )}
            </mapsApi.maps.YMapControls>
            <mapsApi.maps.YMapListener onActionEnd={createBehaviorEventHandler()} />
        </mapsApi.maps.YMap>
    );
});
